import { useChannelContext } from "client/components/channel"
import { RecordingFieldsFragment } from "client/graphql"
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react"

import { useCartContext } from "../commerce/components/cart/CartContext"
import { useShopContext } from "../commerce/components/ShopContext"
import { useGiveawaysContext } from "../giveaways/context/GiveawaysContext"
import { useVideoContext } from "../player/player/VideoContext"
import { useIsSaleActive } from "../target/hooks"
import { useVotingContext } from "../voting/components/VotingContext"

import { useActivePlugins, useIsChatHidden } from "./useActivePlugins"

export type GlobalState = {
  isChatPluginAvailable: boolean

  isCommercePluginAvailable: boolean
  isCommercePluginOpen: boolean

  isGiveawaysPluginAvailable: boolean
  isGiveawaysPluginOpen: boolean

  isVotingPluginAvailable: boolean
  isVotingPluginOpen: boolean

  isTargetPluginAvailable: boolean
  isTargetPluginOpen: boolean

  isReportModalOpen: boolean

  isSidePanelOpen: boolean
  sidePanelSelectedTab: SidePanelTab

  lightboxContent: LightboxContent
}

export enum SidePanelTab {
  CHAT = "CHAT",
  COMMERCE = "COMMERCE",
  NONE = "NONE",
}

export enum Plugin {
  VOTING = "VOTING",
  COMMERCE = "COMMERCE",
  TARGET = "TARGET",
  GIVEAWAYS = "GIVEAWAYS",
}

export enum Modal {
  REPORT = "REPORT",
}

export type LightboxContent =
  | { contentType: "live" }
  | { contentType: "vod"; recording: RecordingFieldsFragment }
  | { contentType: "none" }

export enum GlobalActions {
  SET_CHAT_AVAILABLE = "SET_CHAT_AVAILABLE",
  SET_VOTING_OPEN = "SET_VOTING_OPEN",
  SET_COMMERCE_OPEN = "SET_COMMERCE_OPEN",
  SET_GIVEAWAYS_OPEN = "SET_GIVEAWAYS_OPEN",
  SET_VOTING_AVAILABLE = "SET_VOTING_AVAILABLE",
  SET_COMMERCE_AVAILABLE = "SET_COMMERCE_AVAILABLE",
  SET_GIVEAWAYS_AVAILABLE = "SET_GIVEAWAYS_AVAILABLE",
  SET_TARGET_OPEN = "SET_TARGET_OPEN",
  SET_TARGET_AVAILABLE = "SET_TARGET_AVAILABLE",
  SET_REPORT_MODAL_OPEN = "SET_REPORT_MODAL_OPEN",
  SET_SIDE_PANEL_OPEN = "SET_SIDE_PANEL_OPEN",
  SET_SIDE_PANEL_SELECTED_TAB = "SET_SIDE_PANEL_SELECTED_TAB",
  SET_LIGHTBOX_CONTENT = "SET_LIGHTBOX_CONTENT",
}

type GlobalAction =
  | {
      type: GlobalActions.SET_CHAT_AVAILABLE
      payload: { isAvailable: boolean }
    }
  | { type: GlobalActions.SET_VOTING_OPEN; payload: { isOpen: boolean } }
  | { type: GlobalActions.SET_COMMERCE_OPEN; payload: { isOpen: boolean } }
  | {
      type: GlobalActions.SET_GIVEAWAYS_OPEN
      payload: { isOpen: boolean }
    }
  | { type: GlobalActions.SET_TARGET_OPEN; payload: { isOpen: boolean } }
  | {
      type: GlobalActions.SET_VOTING_AVAILABLE
      payload: { isAvailable: boolean }
    }
  | {
      type: GlobalActions.SET_COMMERCE_AVAILABLE
      payload: { isAvailable: boolean }
    }
  | {
      type: GlobalActions.SET_GIVEAWAYS_AVAILABLE
      payload: { isAvailable: boolean }
    }
  | {
      type: GlobalActions.SET_TARGET_AVAILABLE
      payload: { isAvailable: boolean }
    }
  | {
      type: GlobalActions.SET_REPORT_MODAL_OPEN
      payload: { isOpen: boolean }
    }
  | {
      type: GlobalActions.SET_SIDE_PANEL_OPEN
      payload: { isOpen: boolean }
    }
  | {
      type: GlobalActions.SET_SIDE_PANEL_SELECTED_TAB
      payload: { tab: SidePanelTab }
    }
  | {
      type: GlobalActions.SET_LIGHTBOX_CONTENT
      payload: { content: LightboxContent }
    }

const globalReducer = (
  state: GlobalState,
  action: GlobalAction,
): GlobalState => {
  switch (action.type) {
    case GlobalActions.SET_CHAT_AVAILABLE:
      return {
        ...state,
        isChatPluginAvailable: action.payload.isAvailable,
      }
    case GlobalActions.SET_VOTING_OPEN:
      return {
        ...state,
        isVotingPluginOpen: action.payload.isOpen,
      }
    case GlobalActions.SET_COMMERCE_OPEN:
      return {
        ...state,
        isCommercePluginOpen: action.payload.isOpen,
      }
    case GlobalActions.SET_GIVEAWAYS_OPEN:
      return {
        ...state,
        isGiveawaysPluginOpen: action.payload.isOpen,
      }
    case GlobalActions.SET_TARGET_OPEN:
      return {
        ...state,
        isTargetPluginOpen: action.payload.isOpen,
      }
    case GlobalActions.SET_VOTING_AVAILABLE:
      return {
        ...state,
        isVotingPluginAvailable: action.payload.isAvailable,
        isVotingPluginOpen: action.payload.isAvailable
          ? state.isVotingPluginOpen
          : false,
      }
    case GlobalActions.SET_COMMERCE_AVAILABLE:
      return {
        ...state,
        isCommercePluginAvailable: action.payload.isAvailable,
        isCommercePluginOpen: action.payload.isAvailable
          ? state.isCommercePluginOpen
          : false,
      }
    case GlobalActions.SET_GIVEAWAYS_AVAILABLE:
      return {
        ...state,
        isGiveawaysPluginAvailable: action.payload.isAvailable,
        isGiveawaysPluginOpen: action.payload.isAvailable
          ? state.isGiveawaysPluginOpen
          : false,
      }
    case GlobalActions.SET_TARGET_AVAILABLE:
      return {
        ...state,
        isTargetPluginAvailable: action.payload.isAvailable,
        isTargetPluginOpen: action.payload.isAvailable
          ? state.isTargetPluginOpen
          : false,
      }
    case GlobalActions.SET_REPORT_MODAL_OPEN:
      return {
        ...state,
        isReportModalOpen: action.payload.isOpen,
      }
    case GlobalActions.SET_SIDE_PANEL_OPEN:
      return {
        ...state,
        isSidePanelOpen: action.payload.isOpen,
      }
    case GlobalActions.SET_SIDE_PANEL_SELECTED_TAB:
      return {
        ...state,
        sidePanelSelectedTab: action.payload.tab,
      }
    case GlobalActions.SET_LIGHTBOX_CONTENT:
      return {
        ...state,
        lightboxContent: action.payload.content,
      }
    default:
      return state
  }
}

interface ContextValue {
  state: GlobalState
  setPluginOpen: (plugin: Plugin, isOpen: boolean) => void
  setModalOpen: (modal: Modal, isOpen: boolean) => void

  setSidePanelOpen: (isOpen: boolean) => void
  setSidePanelSelectedTab: (tab: SidePanelTab) => void

  setLightboxContent: (value: LightboxContent) => void
}

const GlobalContext = createContext<ContextValue>(undefined)

const useAutoSelectSidePanelTab = (
  dispatch: (value: GlobalAction) => void,
  isChatPluginAvailable: boolean,
  isCommercePluginAvailable: boolean,
) => {
  useEffect(() => {
    const autoSelectedTab = isChatPluginAvailable
      ? SidePanelTab.CHAT
      : isCommercePluginAvailable
        ? SidePanelTab.COMMERCE
        : SidePanelTab.NONE

    dispatch({
      type: GlobalActions.SET_SIDE_PANEL_SELECTED_TAB,
      payload: { tab: autoSelectedTab },
    })
  }, [isChatPluginAvailable, isCommercePluginAvailable])
}

const useAutoHideSidePanel = (
  dispatch: (value: GlobalAction) => void,
  isSidePanelOpen: boolean,
  isChatPluginAvailable: boolean,
  isCommercePluginAvailable: boolean,
) => {
  const allTabsPreviouslyUnavailable = useRef(false)
  useEffect(() => {
    // If all tabs become unavailable, hide the side panel
    if (
      !allTabsPreviouslyUnavailable.current &&
      !isChatPluginAvailable &&
      !isCommercePluginAvailable
    ) {
      dispatch({
        type: GlobalActions.SET_SIDE_PANEL_OPEN,
        payload: { isOpen: false },
      })
    }
    // If one tab becomes available, show the side panel
    if (
      allTabsPreviouslyUnavailable.current &&
      (isChatPluginAvailable || isCommercePluginAvailable)
    ) {
      dispatch({
        type: GlobalActions.SET_SIDE_PANEL_OPEN,
        payload: { isOpen: true },
      })
    }
    allTabsPreviouslyUnavailable.current =
      !isChatPluginAvailable && !isCommercePluginAvailable
  }, [isSidePanelOpen, isChatPluginAvailable, isCommercePluginAvailable])
}

const GlobalProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const { channelId, live } = useChannelContext()
  const { cart, onCartEvent } = useCartContext()
  const plugins = useActivePlugins()
  const { shop } = useShopContext()
  const { videoElement, setMutedPreview } = useVideoContext()
  const isChatHidden = useIsChatHidden()

  const [isTargetSaleActive] = useIsSaleActive(channelId)

  const { activeQuestion } = useVotingContext()
  const { giveaway } = useGiveawaysContext()

  const [state, dispatch] = useReducer(globalReducer, {
    isChatPluginAvailable: false,
    isCommercePluginAvailable: false,
    isCommercePluginOpen: false,
    isGiveawaysPluginAvailable: false,
    isGiveawaysPluginOpen: false,
    isTargetPluginAvailable: false,
    isTargetPluginOpen: false,
    isVotingPluginAvailable: false,
    isVotingPluginOpen: false,
    isReportModalOpen: false,
    isSidePanelOpen: true,
    sidePanelSelectedTab: SidePanelTab.CHAT,
    lightboxContent: { contentType: "none" },
  })

  // Determines whether chat is available
  useEffect(() => {
    dispatch({
      type: GlobalActions.SET_CHAT_AVAILABLE,
      payload: {
        isAvailable: !isChatHidden,
      },
    })
  }, [isChatHidden])

  // Determines whether commerce is available
  useEffect(() => {
    dispatch({
      type: GlobalActions.SET_COMMERCE_AVAILABLE,
      payload: {
        isAvailable:
          (!!shop?.activeSale &&
            plugins?.commerce?.state === "enabled" &&
            live) ||
          (cart && cart.totalQuantity > 0),
      },
    })
  }, [
    shop?.activeSaleId,
    plugins?.commerce?.state,
    live,
    cart?.totalQuantity,
  ])

  // Determines whether target is available
  useEffect(() => {
    dispatch({
      type: GlobalActions.SET_TARGET_AVAILABLE,
      payload: {
        isAvailable:
          isTargetSaleActive &&
          plugins?.["commerce-v2"]?.state === "enabled" &&
          live,
      },
    })
  }, [isTargetSaleActive, plugins?.["commerce-v2"]?.state, live])

  // Determines whether voting is available
  useEffect(() => {
    dispatch({
      type: GlobalActions.SET_VOTING_AVAILABLE,
      payload: { isAvailable: !!activeQuestion && live },
    })
    // Automatically open voting if commerce isn't already open
    if (activeQuestion && !state.isCommercePluginOpen) {
      dispatch({
        type: GlobalActions.SET_VOTING_OPEN,
        payload: { isOpen: true },
      })
    }
  }, [activeQuestion, live])

  // Determines whether giveaways is available
  useEffect(() => {
    dispatch({
      type: GlobalActions.SET_GIVEAWAYS_AVAILABLE,
      payload: {
        isAvailable: giveaway && live,
      },
    })
  }, [giveaway, live])

  const setPluginOpen = (plugin: Plugin, isOpen: boolean) => {
    switch (plugin) {
      case Plugin.COMMERCE:
        dispatch({
          type: GlobalActions.SET_COMMERCE_OPEN,
          payload: { isOpen },
        })
        break
      case Plugin.TARGET:
        dispatch({
          type: GlobalActions.SET_TARGET_OPEN,
          payload: { isOpen },
        })
        break
      case Plugin.VOTING:
        dispatch({
          type: GlobalActions.SET_VOTING_OPEN,
          payload: { isOpen },
        })
        break
      case Plugin.GIVEAWAYS:
        dispatch({
          type: GlobalActions.SET_GIVEAWAYS_OPEN,
          payload: { isOpen },
        })
        break
      default:
        break
    }
  }

  useEffect(
    () =>
      onCartEvent("return-to-show", () => {
        setPluginOpen(Plugin.COMMERCE, false)
      }),
    [onCartEvent],
  )

  const setModalOpen = (modal: Modal, isOpen: boolean) => {
    switch (modal) {
      case Modal.REPORT:
        dispatch({
          type: GlobalActions.SET_REPORT_MODAL_OPEN,
          payload: { isOpen },
        })
        break
      default:
        break
    }
  }

  const setSidePanelOpen = (isOpen: boolean) => {
    dispatch({
      type: GlobalActions.SET_SIDE_PANEL_OPEN,
      payload: { isOpen },
    })
  }

  const setSidePanelSelectedTab = (tab: SidePanelTab) => {
    dispatch({
      type: GlobalActions.SET_SIDE_PANEL_SELECTED_TAB,
      payload: { tab },
    })
  }

  const setLightboxContent = (content: LightboxContent) => {
    dispatch({
      type: GlobalActions.SET_LIGHTBOX_CONTENT,
      payload: { content },
    })
    // When closing the lightbox view, reset video to 'preview mode' (muted, playing)
    if (content.contentType === "none") {
      setMutedPreview(true)
      if (videoElement) {
        videoElement.muted = true
        videoElement.play()
      }
    }
  }

  // Auto-select side panel tab when needed
  useAutoSelectSidePanelTab(
    dispatch,
    state.isChatPluginAvailable,
    state.isCommercePluginAvailable,
  )

  // Auto hide/show side panel tabs become available/unavailable
  useAutoHideSidePanel(
    dispatch,
    state.isSidePanelOpen,
    state.isChatPluginAvailable,
    state.isCommercePluginAvailable,
  )

  return (
    <GlobalContext.Provider
      value={{
        state,
        setPluginOpen,
        setModalOpen,
        setSidePanelOpen,
        setSidePanelSelectedTab,
        setLightboxContent,
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}

const VodGlobalProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const { channelId } = useChannelContext()
  const { shop } = useShopContext()
  const { videoElement, setMutedPreview } = useVideoContext()
  const isChatHidden = useIsChatHidden()
  const [state, dispatch] = useReducer(globalReducer, {
    isChatPluginAvailable: false,
    isCommercePluginAvailable: false,
    isCommercePluginOpen: false,
    isGiveawaysPluginAvailable: false,
    isGiveawaysPluginOpen: false,
    isTargetPluginAvailable: false,
    isTargetPluginOpen: false,
    isVotingPluginAvailable: false,
    isVotingPluginOpen: false,
    isReportModalOpen: false,
    isSidePanelOpen: true,
    sidePanelSelectedTab: SidePanelTab.CHAT,
    lightboxContent: { contentType: "none" },
  })

  const [isTargetSaleActive] = useIsSaleActive(channelId)

  // Determines whether chat is available
  useEffect(() => {
    dispatch({
      type: GlobalActions.SET_CHAT_AVAILABLE,
      payload: {
        isAvailable: !isChatHidden,
      },
    })
  }, [isChatHidden])

  // Determines whether commerce is available
  useEffect(() => {
    dispatch({
      type: GlobalActions.SET_COMMERCE_AVAILABLE,
      payload: { isAvailable: !!shop?.activeSale },
    })
  }, [shop?.activeSaleId])

  // Determines whether target is available
  useEffect(() => {
    dispatch({
      type: GlobalActions.SET_TARGET_AVAILABLE,
      payload: { isAvailable: isTargetSaleActive },
    })
  }, [isTargetSaleActive])

  // Determines whether giveaways is available
  useEffect(() => {
    dispatch({
      type: GlobalActions.SET_GIVEAWAYS_AVAILABLE,
      payload: {
        // For now, the giveaways plugin is not available for VOD.
        isAvailable: false,
      },
    })
  }, [])

  const setPluginOpen = (plugin: Plugin, isOpen: boolean) => {
    switch (plugin) {
      case Plugin.COMMERCE:
        dispatch({
          type: GlobalActions.SET_COMMERCE_OPEN,
          payload: { isOpen },
        })
        break
      case Plugin.TARGET:
        dispatch({
          type: GlobalActions.SET_TARGET_OPEN,
          payload: { isOpen },
        })
        break
      case Plugin.VOTING:
        dispatch({
          type: GlobalActions.SET_VOTING_OPEN,
          payload: { isOpen },
        })
        break
      case Plugin.GIVEAWAYS:
        dispatch({
          type: GlobalActions.SET_GIVEAWAYS_OPEN,
          payload: { isOpen },
        })
        break
      default:
        break
    }
  }

  const setModalOpen = (modal: Modal, isOpen: boolean) => {
    switch (modal) {
      case Modal.REPORT:
        dispatch({
          type: GlobalActions.SET_REPORT_MODAL_OPEN,
          payload: { isOpen },
        })
        break
      default:
        break
    }
  }

  const setSidePanelOpen = (isOpen: boolean) => {
    dispatch({
      type: GlobalActions.SET_SIDE_PANEL_OPEN,
      payload: { isOpen },
    })
  }

  const setSidePanelSelectedTab = (tab: SidePanelTab) => {
    dispatch({
      type: GlobalActions.SET_SIDE_PANEL_SELECTED_TAB,
      payload: { tab },
    })
  }

  const setLightboxContent = (content: LightboxContent) => {
    dispatch({
      type: GlobalActions.SET_LIGHTBOX_CONTENT,
      payload: { content },
    })
    // When closing the lightbox view, reset video to 'preview mode' (muted, playing)
    if (content.contentType === "none") {
      setMutedPreview(true)
      if (videoElement) {
        videoElement.muted = true
        videoElement.play()
      }
    }
  }

  // Auto-select side panel tab when needed
  useAutoSelectSidePanelTab(
    dispatch,
    state.isChatPluginAvailable,
    state.isCommercePluginAvailable,
  )

  // Auto hide/show side panel tabs become available/unavailable
  useAutoHideSidePanel(
    dispatch,
    state.isSidePanelOpen,
    state.isChatPluginAvailable,
    state.isCommercePluginAvailable,
  )

  return (
    <GlobalContext.Provider
      value={{
        state,
        setPluginOpen,
        setModalOpen,
        setSidePanelOpen,
        setSidePanelSelectedTab,
        setLightboxContent,
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}

const useGlobalContext = () => {
  const globalContext = useContext(GlobalContext)
  if (!globalContext) {
    throw new Error(
      "No GlobalContext.Provider found when calling useGlobalContext.",
    )
  }
  return globalContext
}

export {
  GlobalProvider,
  useGlobalContext,
  GlobalContext,
  VodGlobalProvider,
}
